import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import api from "../../services/api";
import Button from "../Buttons/Button";
import { cardStyles } from "../VenueCard/cardStyles";

// ZONA's fee for a venue. On Edit Venue it loads and saves itself (venueId). On the
// approve-access screen it's controlled (value / onChange) and saved with the approval.
// Blank = the standard rate.
const EMPTY = { fee_percent: "", drop_fee_percent: "", billing_email: "" };

export default function VenueFeeFields({ venueId, value, onChange }) {
  const controlled = typeof onChange === "function";
  const [own, setOwn] = useState(EMPTY);
  const [standard, setStandard] = useState({ fee: 15, drop: 20 });
  const [saving, setSaving] = useState(false);
  // Save stays off until the venue's saved fee has loaded, so a failed load can't
  // save blanks over a real rate.
  const [loadState, setLoadState] = useState(venueId ? "loading" : "none");

  const current = controlled ? value || EMPTY : own;
  const set = (next) => (controlled ? onChange(next) : setOwn(next));
  const latest = useRef(current);
  latest.current = current;

  useEffect(() => {
    if (!venueId) return;
    api.get(`/venues/${venueId}/fee`)
      .then(({ data }) => {
        setStandard({ fee: data.standard_fee_percent, drop: data.standard_drop_fee_percent });
        const saved = {
          fee_percent: data.set_fee_percent == null ? "" : String(data.set_fee_percent),
          drop_fee_percent: data.set_drop_fee_percent == null ? "" : String(data.set_drop_fee_percent),
          billing_email: data.billing_email || "",
        };
        // Only fill what hasn't been typed yet.
        const now = latest.current;
        set(Object.fromEntries(Object.keys(saved).map((k) => [k, String(now[k] || "").trim() ? now[k] : saved[k]])));
        setLoadState("loaded");
      })
      .catch(() => setLoadState("failed"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [venueId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/venues/${venueId}/fee`, current);
      toast.success("Fee saved.");
    } catch (err) {
      toast.error(err?.response?.data?.message || "Could not save the fee.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.card}>
      <div style={styles.label}>ZONA fee</div>
      <p style={styles.help}>
        The venue sees this in their business panel. Leave blank for the standard rate. Venues that take
        payment themselves get a monthly invoice, on the 5th, for tables their guests attended. It goes to the
        billing email.
      </p>
      <div style={styles.row}>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Fee (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            inputMode="decimal"
            placeholder={String(standard.fee)}
            value={current.fee_percent}
            onChange={(e) => set({ ...current, fee_percent: e.target.value })}
            style={styles.input}
          />
        </label>
        <label style={styles.field}>
          <span style={styles.fieldLabel}>Drop / Last Call fee (%)</span>
          <input
            type="number"
            min="0"
            max="100"
            step="0.5"
            inputMode="decimal"
            placeholder={String(standard.drop)}
            value={current.drop_fee_percent}
            onChange={(e) => set({ ...current, drop_fee_percent: e.target.value })}
            style={styles.input}
          />
        </label>
      </div>
      <label style={{ ...styles.field, marginTop: 14 }}>
        <span style={styles.fieldLabel}>Billing email</span>
        <input
          type="email"
          placeholder="Where the monthly invoice goes"
          value={current.billing_email}
          onChange={(e) => set({ ...current, billing_email: e.target.value })}
          style={styles.input}
        />
      </label>
      {loadState === "failed" && (
        <p style={{ ...styles.help, color: "#ff6b6b", margin: "12px 0 0" }}>
          Couldn't load this venue's fee. Reload before saving.
        </p>
      )}
      {!controlled && venueId && (
        <div style={{ marginTop: 14 }}>
          <Button primaryBtn onClick={save} disabled={saving || loadState !== "loaded"}>
            {saving ? "Saving…" : "Save fee"}
          </Button>
        </div>
      )}
    </div>
  );
}

const styles = cardStyles;
